import { useEffect, useState } from "react";
import { User, Phone as PhoneIcon, Mail, Lock, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatDateTimeBR } from "../lib/api";
import { maskPhoneBR, sanitizePhone } from "../lib/phone";
import { useAuth } from "../context/AuthContext";

export default function MeuPerfil() {
  const { setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [form, setForm] = useState({ name: "", last_name: "", phone: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        setProfile(data);
        setForm({
          name: data.name || "",
          last_name: data.last_name || "",
          phone: maskPhoneBR(data.phone || ""),
        });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao carregar perfil");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/me/profile", {
        name: form.name.trim(),
        last_name: form.last_name.trim(),
        phone: sanitizePhone(form.phone),
      });
      setProfile(data);
      setUser((u) => (u ? { ...u, name: data.name, phone: data.phone } : u));
      toast.success("Perfil atualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setSavingPassword(true);
    try {
      await api.post("/me/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Senha alterada com sucesso");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao trocar senha");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-gray-400" data-testid="perfil-loading">Carregando…</div>;
  if (!profile) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="meu-perfil-page">
      <div>
        <h1 className="font-display text-3xl text-[#0A1128] font-light">Meu Perfil</h1>
        <p className="text-sm text-gray-500 mt-1">
          Suas informações pessoais e credenciais de acesso.
        </p>
      </div>

      {/* Info card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#0A1128] text-white flex items-center justify-center font-bold text-xl" data-testid="perfil-avatar">
            {(profile.name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-lg font-semibold text-[#0A1128]" data-testid="perfil-name">{profile.name} {profile.last_name || ""}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5"><ShieldCheck size={12} /> {profile.role_label}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              Último acesso: {profile.last_login_at ? formatDateTimeBR(profile.last_login_at) : "—"}
            </div>
          </div>
        </div>

        <form onSubmit={saveProfile} className="space-y-3" data-testid="perfil-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1"><User size={12} /> Nome</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="perfil-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Sobrenome</label>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="perfil-lastname-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1"><Mail size={12} /> E-mail</label>
            <input readOnly value={profile.email} className="w-full mt-1 border border-gray-100 bg-gray-50 rounded px-3 py-2 text-sm text-gray-500" />
            <p className="text-[10px] text-gray-400 mt-1">O e-mail só pode ser alterado por um administrador.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1"><PhoneIcon size={12} /> Telefone (WhatsApp)</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhoneBR(e.target.value) })} type="tel" inputMode="tel" autoComplete="tel" placeholder="(21) 99999-9999" className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="perfil-phone-input" />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={savingProfile} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1128] px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#c9a534] disabled:opacity-50" data-testid="perfil-save-btn">
              <Save size={14} /> {savingProfile ? "Salvando…" : "Salvar perfil"}
            </button>
          </div>
        </form>
      </div>

      {/* Password card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="font-display text-xl text-[#0A1128] flex items-center gap-2 mb-4">
          <Lock size={16} className="text-[#D4AF37]" /> Alterar senha
        </h2>
        <form onSubmit={changePassword} className="space-y-3" data-testid="perfil-password-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Senha atual</label>
            <input required type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm font-mono" data-testid="perfil-pw-current" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Nova senha</label>
              <input required type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm font-mono" data-testid="perfil-pw-next" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Confirmar nova senha</label>
              <input required type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm font-mono" data-testid="perfil-pw-confirm" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500">
            Mínimo 8 caracteres, com maiúscula, minúscula, número e símbolo.
          </p>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={savingPassword} className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#14213D] disabled:opacity-50" data-testid="perfil-pw-save">
              <Lock size={14} /> {savingPassword ? "Alterando…" : "Alterar senha"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
